import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import BandwidthUsageBanner from "@/components/banners/BandwidthUsageBanner";
import CustomizeButton from "@/components/CustomizeButton";
import { RouteButton } from "@/components/common/styles";
import { makePlanName } from "@/components/subscription/customize/PricingBanner/helpers";
import { PlanType } from "@/types/PlanTypes";
import getProxyPlanKey from "@/utils/getProxyPlanKey";
import { getBandwidthUsageState } from "@/utils/getBandwidthUsageState";
import { userFormatCurrency, userReadableBytes } from "@/utils/textUtils";
import useSubscriptionStats from "@/hooks/useSubscriptionStats";
import ProductPlanHeader from "./ProductPlanHeader";
import { DashboardProduct } from "./useDashboardProducts";

/**
 * The Product Overview card outline from the design: a 1.5px neutral stroke
 * (shared with the canceled-product card) instead of the flat dashboard card.
 */
export const productOverviewCardSx: SxProps<Theme> = {
  border: 1.5,
  borderColor: "neutral.focusVisible",
};

function StatTile({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <Grid size={{ xs: 12, md: 6, lg: 4 }}>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
          height: "100%",
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {value ?? <Skeleton width={120} />}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Grid>
  );
}

const BYTES_IN_GB = 1024 ** 3;

/**
 * The plan's limit is always printed in GB, so the used side never climbs
 * past GB either: "1,024 GB / 5,000 GB", not "1 TB / 5,000 GB" (R-2320).
 * Under a GB it keeps the smaller unit, as the design prints "112 MB / 1 GB".
 */
function formatUsedBytes(bytes: number) {
  if (bytes < BYTES_IN_GB) return userReadableBytes(bytes);
  const gb = bytes / BYTES_IN_GB;
  return `${gb.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
}

function formatUsedBandwidth(plan: PlanType, bandwidthTotal: number) {
  const used = formatUsedBytes(bandwidthTotal);
  const limit =
    plan.bandwidth_limit === 0
      ? "∞ GB"
      : `${plan.bandwidth_limit.toLocaleString()} GB`;
  return `${used} / ${limit}`;
}

function formatSuccessRate(requestsTotal: number, requestsSuccessful: number) {
  if (requestsTotal === 0) return "0%";
  return `${((requestsSuccessful / requestsTotal) * 100).toFixed(1)}%`;
}

function UsageOverviewHeader({ planId }: { planId: string }) {
  const theme = useTheme();
  // Per the design annotations, View Full Statistics turns into an outlined
  // button on tablet and mobile for a clearer clickable area.
  const isCompactView = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={2}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body1" fontWeight={500}>
          Usage overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This billing cycle
        </Typography>
      </Stack>
      <RouteButton
        variant={isCompactView ? "outlined" : "text"}
        color="neutral"
        size="small"
        endIcon={<ChevronRightIcon fontSize="small" />}
        to="/$planId/stats/$tab"
        params={{ planId, tab: "bandwidth" }}
      >
        View Full Statistics
      </RouteButton>
    </Stack>
  );
}

function UsageOverview({ plan }: { plan: PlanType }) {
  const stats = useSubscriptionStats(plan.id);

  const usageState = getBandwidthUsageState({
    limit: plan.bandwidth_limit,
    total: stats?.bandwidth_total,
    projected: stats?.bandwidth_projected,
  });

  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <UsageOverviewHeader planId={String(plan.id)} />
      <Grid container spacing={2}>
        <StatTile
          value={stats && formatUsedBandwidth(plan, stats.bandwidth_total)}
          label="Bandwidth used"
        />
        <StatTile
          value={stats?.requests_total.toLocaleString()}
          label="Total requests"
        />
        <StatTile
          value={
            stats &&
            formatSuccessRate(stats.requests_total, stats.requests_successful)
          }
          label="Success rate"
        />
      </Grid>
      {usageState ? (
        <BandwidthUsageBanner
          planId={plan.id}
          usageState={usageState}
          source="dashboard-upgrade"
          actionVariant="text"
        />
      ) : null}
    </Stack>
  );
}

function OverviewActions({
  product,
  plan,
}: {
  product: DashboardProduct;
  plan: PlanType;
}) {
  const isFree = plan.proxy_type === "free";
  const planId = String(plan.id);

  return (
    <Stack
      direction="row"
      spacing={2}
      flexWrap="wrap"
      useFlexGap
      sx={{ width: { xs: "100%", md: "auto" } }}
    >
      {product.type === "residential" ? (
        <RouteButton
          variant="outlined"
          color="neutral"
          to="/$planId/proxy/endpoint-generator"
          params={{ planId }}
        >
          Endpoint Generator
        </RouteButton>
      ) : null}
      <RouteButton
        variant="outlined"
        color="neutral"
        to="/$planId/proxy/list"
        params={{ planId }}
      >
        Proxy List
      </RouteButton>
      {isFree ? (
        <RouteButton
          variant="contained"
          color="primary"
          to="/subscription/choose-plan"
        >
          Upgrade Plan
        </RouteButton>
      ) : (
        <CustomizeButton
          variant="contained"
          color="primary"
          customizeData={{
            source: "dashboard-upgrade",
            existingPlanId: plan.id,
          }}
        >
          Upgrade Plan
        </CustomizeButton>
      )}
    </Stack>
  );
}

/**
 * The active-product overview card (r-1776): plan header with product-specific
 * actions, the billing-cycle usage overview, and the product's own bandwidth
 * alert directly below the usage metrics.
 */
export default function ProductOverviewCard({
  product,
}: {
  product: DashboardProduct;
}) {
  const plan = product.plan;
  if (!plan) return null;

  const isFree = plan.proxy_type === "free";

  return (
    <Card
      sx={productOverviewCardSx}
      data-testid={`product-overview-${product.type}`}
    >
      <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
        <ProductPlanHeader
          icon={product.icon}
          title={product.title}
          exclusivity={makePlanName(getProxyPlanKey(plan))[1]}
          caption={
            isFree
              ? "free/month"
              : `${userFormatCurrency(plan.monthly_price)}/month`
          }
          actions={<OverviewActions product={product} plan={plan} />}
        />
      </Box>
      <UsageOverview plan={plan} />
    </Card>
  );
}
